import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { getToken, getAuthMode } from '../../lib/auth.ts';

interface RequireModeProps {
  /** "admin"  → accessible only in admin mode (super_admin, not impersonating)
   *  "tenant" → accessible only in tenant mode (regular user OR impersonating super_admin) */
  mode: 'admin' | 'tenant';
  children: ReactNode;
}

/**
 * Route guard that enforces the current auth mode.
 * - Always redirects to /login if no token is present.
 * - Redirects admin→/admin if a super_admin tries to access tenant routes.
 * - Redirects tenant→/ if a tenant-mode user tries to access admin routes.
 */
export function RequireMode({ mode, children }: RequireModeProps) {
  if (!getToken()) return <Navigate to="/login" replace />;

  const current = getAuthMode();

  if (mode === 'admin' && current !== 'admin') {
    // Tenant-scoped user or impersonating super_admin trying to access admin pages
    return <Navigate to="/" replace />;
  }

  if (mode === 'tenant' && current !== 'tenant') {
    // Pure super_admin trying to access tenant pages → go to admin overview
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}
